import { RequireExactlyOne } from 'type-fest';

import {
  AttachmentAnalysisUploadStepFields,
  CodeBlockStepFields,
  GlobalContextVariable,
  HandoffConfiguration,
} from '../slices/canvas-workflow-builder/types/dataTypes';
import { DiscoverAutomationsByTypeResponse } from './DiscoverTypes';
import { SolveWidgetProduct } from './types';
import { DropdownOptionsType } from 'src/pages/solve-workflows/go-to-step/GoToStepTypes';
import { WorkflowVersionType } from 'src/pages/workflow-builder-edit/autonomous-agent/constants';
import { WorkflowTypes } from 'src/pages/workflow-builder-edit/types';
import { DiscoverTopic } from 'src/reducers/discoverReducer/types';
import {
  ConditionExpressions,
  DropoffBreakdown,
  RelevanceBreakdown,
} from 'src/services/apiInterfaces';
import { EventProperty } from 'src/services/custom-events/types';
import { ABTestVariantInfo } from 'src/services/insights/types';
import { Action, ContextVariable } from 'src/types/actionBuilderApiTypes';
import {
  ElasticSearchDataType,
  StepTypes,
  TranslationsTableMode,
} from 'src/utils/enums';

export type EmailConversaionChannel = 'live';

export type ConversationChannel =
  | 'email'
  | 'widget'
  | 'api'
  | 'slack'
  | 'voice'
  | 'playbook'
  | 'web_call';
export type TranslationChannel = ConversationChannel | 'interactive_email';

export type AllConversationChannel =
  | ConversationChannel
  | EmailConversaionChannel
  | 'sunshine'
  | 'preview';

export const channelToDisplayName: Record<ConversationChannel, string> = {
  api: 'Headless',
  email: 'Email',
  playbook: 'Assist',
  slack: 'Slack',
  voice: 'Voice',
  web_call: 'Web Call',
  widget: 'Chat',
};

export type WorkflowBuilderHeaderChannel =
  | 'workflows'
  | 'chats'
  | 'conversations' // currently 'conversations' is for the email channel, 'chats' is for widget.
  | 'templates'
  | 'inbound' // voice inbound workflows
  | 'outbound'; // voice outbound workflows

export type SubflowType = 'voice_outbound' | null;

export type ChannelMode = 'inbound' | 'outbound' | 'inbound_web_call';

export interface ActionSettingsCustomization {
  action_type: string;
  configuration_fields:
    | FreshdeskTicketAndContactCreationCustomizationSettings
    | ZendeskHandoffCustomization
    | ZendeskTicketCreationCustomization
    | ZendeskMessagingHandoffActionSettings
    | IntercomHandoffSettings
    | SalesforceHandoffCustomization
    | SalesforceCaseCreationCustomization
    | SalesforceChatHandoffCustomization
    | ArticleSuggestionActionSettings
    | GranularArticleSuggestionActionV2Settings
    | CatchAllHandoffCustomization
    | JiraServiceManagementCreateRequestSettings
    | ZendeskChatHandoffCustomization
    | HyperlinkRedirectCustomizationSettings
    | KustomerHandoffCustomization
    | KustomerChatHandoffCustomization
    | KustomerConversationCreationCustomization
    | TriggerEventCustomizationSettings
    | FreshdeskTicketCreationCustomizationSettings
    | SnapEngageChatHandoffActionSettings
    | GorgiasChatHandoffActionSettings
    | GorgiasTicketCreationCustomizationSettings
    | PylonTicketCreationCustomizationSettings
    | TalkdeskChatHandoffActionSettings
    | SalesforceMessagingHandoffActionSettings
    | ContextVariableMappingActionSettings
    | ZendeskAttachmentUploadActionSettings
    | DynamicArticleSuggestionInitialSettings
    | ParseJwtCustomizationSettings
    | SetContextVariableCustomizationSettings
    | ForethoughtLiveChatHandoffActionSettings
    | DynamicCardCustomizationSettings
    | ProductRecommendationCustomizationSettings
    | IntercomTicketCreationCustomizationSettings
    | FrontTicketCreationCustomizationSettings
    | ZammadTicketCreationCustomizationSettings
    | IntercomLiveChatActionSettings
    | ConversationOauthSignInCustomizationSettings
    | ZendeskTalkHandoffCustomization
    | ZendeskSipCallCustomization
    | GladlyChatHandoffSettings
    | CustomEventCustomizationSettings
    | GranularJiraServiceManagementCreateRequestSettings
    | HubspotTicketCreationSettings
    | HubspotChatHandoffSettings
    | Dynamics365CaseCreationSettings
    | Comm100ChatHandoffSettings
    | QuiqChatHandoffActionSettings
    | CallCorpChatHandoffActionSettings
    | ServicenowCaseIncidentCreationSettings;
}

export interface CatchAllHandoffCustomization {
  custom_message: null | string;
  show_link_preview?: boolean;
}

export interface ZendeskOrSalesforceContextVariables {
  email: string | null;
  name: string | null;
  question: string | null;
}

export interface ZendeskChatHandoffCustomization extends ZendeskOrSalesforceContextVariables {
  available_zd_chat_departments: Array<{
    id: string;
    name: string;
  }>;
  chat_tags?: string[];
  email: string;
  hide_zd_widget_delay?: number;
  name: string;
  one_chat?: boolean;
  question: string;
  zd_chat_department: {
    id: string;
    name: string;
  } | null;
}

export interface ZendeskTalkHandoffCustomization {
  jwt_token: string;
  line_id: string;
}

export interface ZendeskSipCallCustomization {
  email: string;
  failure_message?: string;
  name: string;
  sip_uri: string;
  subject: string;
  tags: string[] | null;
}

export interface SuncoTicketCustomizationConfig {
  custom_fields_and_values: ZendeskCustomFieldAndValue[];
  ticket_custom_fields: ZendeskTicketCustomField[];
}

export interface SuncoAvailableConnector {
  connector_id: string;
  display_name: string;
}

export interface SuncoLiveChatCustomization extends ZendeskOrSalesforceContextVariables {
  // Server-only: the org's Sunco connectors, used to populate the connector
  // selector. Stripped from editable state before saving.
  available_connectors?: SuncoAvailableConnector[];
  available_zd_chat_departments: Array<{
    id: string;
    name: string;
  }>;
  chat_tags?: string[];
  // Pins the handoff to a specific Sunco app; null/undefined uses the default.
  connector_id?: string | null;
  email: string;
  jwt?: string | null;
  name: string;
  placeholder_message?: string;
  question: string;
  sunco_ticket_customization_config?: SuncoTicketCustomizationConfig;
  zd_chat_department: {
    id: string;
    name: string;
  } | null;
}

export interface ZendeskTicketCustomField {
  custom_field_options: Array<{ name: string; value: string }>;
  id: number | string;
  title: string;
  type: string;
}

export interface ZendeskTicketField {
  field_options: Array<{ name: string; value: string }>;
  id: string;
  title: string;
  type: string;
}

export interface ZendeskCustomFieldAndValue {
  id: number | string;
  value: string | number;
}

export interface TicketStaticFieldAndValue {
  id: string;
  value: string | number;
}

export interface ZendeskAvailableConnector {
  connector_id: string;
  display_name: string;
}

export interface ZendeskCommonCustomization {
  // Server-only: the org's Zendesk connectors, used to populate the connector
  // selector. Named distinctly because available_connectors means Sunshine
  // connectors on the same handoff response. Stripped before saving.
  available_zendesk_connectors?: ZendeskAvailableConnector[];
  // Pins the action to a specific Zendesk connector; null/undefined uses the default.
  connector_id?: string | null;
  ticket_creation_settings: {
    ticket_creation_config: {
      allow_attachment_upload: boolean;
      available_groups: ZendeskGroup[];
      available_priorities: {
        label: string;
        value: string;
      }[];
      available_statuses: {
        label: string;
        value: string;
      }[];
      available_ticket_brands: string[];
      available_types: {
        label: string;
        value: string;
      }[];
      custom_fields_and_values: ZendeskCustomFieldAndValue[];
      group_id: number | null;
      priority: string | null;
      status: string | null;
      ticket_brand: null | string;
      ticket_custom_fields: ZendeskTicketCustomField[];
      ticket_tags: string[];
      ticket_title: null | string;
      transcript_comment_visibility?: string;
      type: string | null;
      update_flamethrower_conversation_deflection_status: null | boolean;
    };
  };
}

interface ZendeskGroup {
  default?: boolean;
  deleted?: boolean;
  description?: string;
  group_id: number;
  is_public?: boolean;
  name: string;
}

export interface ZendeskHandoffCustomization extends ZendeskCommonCustomization {
  agent_chat_handoff_settings: {
    available_zd_chat_departments: Array<{
      id: string;
      name: string;
    }>;
    chat_tags?: string[];
    zd_chat_department: {
      id: string;
      name: string;
    } | null;
  };
  include_agent_chat_handoff: boolean;
  include_ticket_creation_handoff: boolean;
  sunco_ticket_customization_config?: SuncoTicketCustomizationConfig;
  ticket_creation_settings: {
    create_ticket: boolean;
    custom_message: null | string;
    ticket_creation_config: {
      allow_attachment_upload: boolean;
      available_groups: ZendeskGroup[];
      available_priorities: {
        label: string;
        value: string;
      }[];

      available_statuses: {
        label: string;
        value: string;
      }[];
      available_ticket_brands: string[];
      available_types: {
        label: string;
        value: string;
      }[];
      confirmation_message: string;
      custom_fields_and_values: ZendeskCustomFieldAndValue[];
      group_id: number | null;
      handoff_form_question_message: string;
      priority: string | null;
      status: string | null;
      ticket_brand: null | string;
      ticket_custom_fields: ZendeskTicketCustomField[];
      ticket_tags: string[];
      ticket_title: null | string;
      transcript_comment_visibility?: string;
      type: string | null;
      update_flamethrower_conversation_deflection_status: null | boolean;
    };
  };
}

export interface ZendeskTicketCreationCustomization extends ZendeskCommonCustomization {
  email: string | null;
  name: string | null;
  question: string | null;
  ticket_id_output_variable: string | null;
}

interface FrontItems {
  id: string;
  name: string;
}

interface HaloCRMCategory {
  id: string;
  name: string;
  type_id: string;
}

export interface FrontTicketCreationCustomizationSettings {
  channel_id?: string;
  contact_email_variable?: string;
  contact_name_variable?: string;
  custom_fields?: Record<string, string | number | boolean> | null;
  front_channels?: FrontItems[];
  front_custom_fields?: FrontItems[];
  ticket_id_output_variable?: string;
}

interface ZammadCustomItems {
  name: string;
  value: string;
}

export interface ZammadTicketCreationCustomizationSettings {
  customer_email_variable: string;
  group_id: number;
  priority_id?: number;
  severity?: string;
  state_id?: number;
  ticket_hierarchy?: string;
  ticket_id_output_variable?: string;
  title_variable: string;
  zammad_groups?: FrontItems[];
  zammad_severities?: ZammadCustomItems[];
  zammad_ticket_hierarchies?: ZammadCustomItems[];
  zammad_ticket_priorities?: FrontItems[];
  zammad_ticket_states?: FrontItems[];
}

export interface HaloCRMTicketCreationCustomizationSettings {
  categories?: Record<string, number>;
  contact_email_variable?: string;
  contact_name_variable?: string;
  custom_fields?: Record<string, string | number | boolean>;
  details_variable: string;
  halocrm_categories?: HaloCRMCategory[];
  halocrm_custom_fields?: FrontItems[];
  halocrm_priorities?: Record<string, string | number>;
  halocrm_slas?: FrontItems[];
  halocrm_tags?: FrontItems[];
  halocrm_teams?: FrontItems[];
  halocrm_ticket_types?: FrontItems[];
  include_transcript: boolean;
  priority_id?: number;
  sla_id?: number;
  summary_variable: string;
  tags?: Record<string, string>;
  team_id?: number;
  ticket_id_output_variable?: string;
  type_id?: number;
}

export interface GorgiasTicketCreationCustomizationSettings {
  contact_email_variable?: string;
  contact_name_variable?: string;
  custom_fields?: Array<{
    id: number;
    value: string;
  }>;
  gorgias_custom_fields?: Array<{
    custom_field_options: Array<{ name: string; value: string }>;
    id: number;
    title: string;
    type: string;
  }>;
  include_transcript?: boolean;
  priority?: string;
  subject_variable?: string;
  summary_variable?: string;
  tags?: string[];
  ticket_id_output_variable?: string;
}

export interface PylonTicketCreationCustomizationSettings {
  assignee_id?: string;
  // Priority dropdown options ({ label, value }), returned by the API.
  available_priorities?: Array<{ label: string; value: string }>;
  available_teams?: Array<{ id: string; name: string }>;
  available_users?: Array<{ email: string; id: string; name: string }>;
  contact_email_variable?: string;
  contact_name_variable?: string;
  // Configured custom fields, keyed by Pylon's string slug.
  custom_fields?: Array<{ slug: string; value: string }>;
  priority?: string;
  // Issue custom field definitions, returned by the API for the dropdowns.
  pylon_custom_fields?: Array<{
    label: string;
    select_options: Array<{ label: string; slug: string }>;
    slug: string;
    type: string;
  }>;
  tags?: string[];
  team_id?: string;
  ticket_id_output_variable?: string;
  ticket_title?: string;
  update_flamethrower_conversation_deflection_status?: boolean;
}

export type ReadArticleActionOptions = 'new_tab' | 'expand_in_widget' | 'none';
export interface BaseArticleConfigurationFields {
  allow_generative_ai: boolean;
  knowledge_configuration_prompt: string | null;
  should_override_global_knowledge_configuration_prompt: boolean;
}
export interface GranularArticleSuggestionActionV2Settings extends Partial<BaseArticleConfigurationFields> {
  allow_generative_ai: boolean;
  article_sources: Array<ArticleSource>;
  available_article_sources: Array<ArticleSourceOption>;
  hand_off_when_not_found?: boolean;
  helpful_button_text?: string;
  is_last_element: boolean;
  not_helpful_button_text?: string;
  question: string;
  read_article_action: string;
  read_article_action_options: {
    label: string;
    value: ReadArticleActionOptions;
  }[];
  show_buttons?: boolean;
  top_n_articles: number;
}

export interface ExtraPrechatFormDetails {
  label: string;
  transcript_fields: string[];
  value: string;
}

export interface FieldAndValue {
  id: string;
  is_custom?: boolean;
  value: string | boolean;
}

export interface SalesforceAvailableConnector {
  connector_id: string;
  display_name: string;
}

export interface SalesforceCommonCustomization {
  available_connectors?: SalesforceAvailableConnector[];
  connector_id?: string;
  ticket_creation_settings: {
    ticket_creation_config: {
      fields_and_values: Array<FieldAndValue>;
      ticket_fields: Array<{
        field_options: Array<{ name: string; value: string }>;
        id: string;
        is_custom?: boolean;
        title: string;
        type: string;
      }>;
      ticket_title: string;
      update_flamethrower_conversation_deflection_status: null | boolean;
    };
  };
}

export interface SalesforceHandoffCustomization extends SalesforceCommonCustomization {
  agent_chat_handoff_settings: {
    button_id: string;
    chat_transcript_text_area_fields: Array<{
      id: string;
      title: string;
    }> | null;
    deployment_id: string;
    extra_prechat_form_details: Array<ExtraPrechatFormDetails>;
    form_prompt: string;
    handoff_success_message: string;
    ignore_name_email: boolean;
    no_agents_available_message: string;
    store_transcript_to_chat_transcript_field_id: string | null;
    talk_to_agent_button_prompt: string;
  };
  include_agent_chat_handoff: boolean;
  include_ticket_creation_handoff: boolean;
  ticket_creation_settings: {
    create_ticket: boolean;
    custom_message: string;
    ticket_creation_config: {
      confirmation_message: string;
      fields_and_values: Array<{
        id: string;
        is_custom?: boolean;
        value: string;
      }>;
      handoff_form_question_message: string;
      ticket_fields: Array<{
        field_options: Array<{ name: string; value: string }>;
        id: string;
        is_custom?: boolean;
        title: string;
        type: string;
      }>;
      ticket_title: string;
      update_flamethrower_conversation_deflection_status: null | boolean;
    };
  };
}

export interface SalesforceChatHandoffCustomization {
  agent_chat_handoff_settings: {
    button_id: string;
    chat_transcript_text_area_fields: Array<{
      id: string;
      title: string;
    }> | null;
    deployment_id: string;
    extra_prechat_form_details: Array<ExtraPrechatFormDetails>;
    store_transcript_to_chat_transcript_field_id: string | null;
  };
  email: string | null;
  name: string | null;
  question: string | null;
}

export interface SalesforceCaseCreationCustomization extends SalesforceCommonCustomization {
  add_transcript_comment: boolean;
  attachment_share_type?: 'V' | 'I';
  case_number_output_variable: string | null;
  create_email_message: boolean;
  email: string | null;
  name: string | null;
  question: string | null;
  ticket_id_output_variable: string | null;
  transcript_format?: 'html' | 'plain_text';
}

export interface IntercomHandoffSettings {
  intercom_handoff_settings: {
    ask_for_visitor_email: boolean;
    ask_for_visitor_name: boolean;
    ask_for_visitor_question: boolean;
    failure_message: string;
    form_prompt: string;
    identity_verification_enabled: boolean;
    include_transcript: boolean;
  };
}

export interface HyperlinkRedirectCustomizationSettings {
  destination_link: string;
  redirect_option: string;
}

export interface TriggerEventCustomizationSettings {
  context_variables_to_update: string[];
  event_name: string;
}

export interface ContextVariableJwtMapping {
  cv_id: string;
  jwt_key: string;
}

export interface ParseJwtCustomizationSettings {
  context_variable_jwt_mappings?: ContextVariableJwtMapping[];
  context_variables_to_update: string[];
  encoded_context_variable: string;
  key: string;
}

export interface FreshdeskTicketCreationCustomizationSettings {
  ticket_creation_settings: {
    custom_fields?: { [key: string]: string | number | boolean } | null;
    form_prompt: string;
    group_id?: number | string;
    priority?: number | string;
    product_id?: number | string;
    status?: number | string;
    tags: string[];
    ticket_creation_success_message: string;
    transcript_field?: string;
    type?: string;
    upload_files_attached: boolean;
  };
}

export interface FreshdeskTicketAndContactCreationCustomizationSettings {
  company_id?: number | string;
  custom_fields?: { [key: string]: string | number | boolean } | null;
  description: string;
  email: string;
  group_id?: number | string;
  name: string;
  priority?: number | string;
  product_id?: number | string;
  status?: number | string;
  subject: string;
  tags: string[];
  ticket_creation_success_message: string;
  transcript_field?: string;
  type?: string;
  upload_files_attached: boolean;
}

interface FreshdeskTicketOption {
  label: string;
  value: string;
}

export interface FreshdeskTicketFields {
  companies: FreshdeskTicketOption[];
  groups: FreshdeskTicketOption[];
  priorities: FreshdeskTicketOption[];
  products: FreshdeskTicketOption[];
  statuses: FreshdeskTicketOption[];
  types: FreshdeskTicketOption[];
}

interface HubspotTicketProperty {
  createdUserId?: string | null;
  fieldType: string;
  groupName?: string | null;
  label: string;
  modificationMetadata?: Record<string, boolean> | null;
  name: string;
  options?: { label: string; value: string }[] | null;
  type: string;
}

export interface HubspotTicketProperties {
  custom_ticket_properties: Array<HubspotTicketProperty>;
  priority_options: Array<{ label: string; value: string }>;
}

export interface Comm100CustomVariables {
  custom_variables: Comm100CustomVariable[];
}

export interface Comm100CustomVariable {
  hyperlink: string;
  id: string;
  name: string;
  type: string;
  value: string;
}
export interface SetContextVariableCustomizationSettings {
  mappings: {
    context_variable_id: string;
    value: ContextVariableValue;
  }[];
}

type Properties = {
  default_cv_id?: string | null;
  property_id: string;
  value?: string | number | boolean | null;
}[];

export interface CustomEventToolConfigurationFields {
  event_configuration_id: string | null;
  properties: Properties | null;
}

export interface CustomEventCustomizationSettings extends CustomEventToolConfigurationFields {
  available_properties?: Properties;
  event_configuration_id: string;
  properties: Properties;
}

export interface DynamicCardCustomizationSettings {
  content_config?: { limit: number } | null;
  display_option: { limit: number; type: 'capped' | 'all' };
  dynamic_list_context_variable?: string | null;
  /** Whether the fallback "None of these" option is enabled for branching */
  fallback_enabled?: boolean;
  /** Text shown for the fallback option (default: "None of these") */
  fallback_text?: string | null;
  hide_free_form_query?: boolean;
  /** Only applies when the bound CV is a multi-select dynamic list */
  max_selections?: number | null;
  message?: string | null;
  /** Only applies when the bound CV is a multi-select dynamic list */
  min_selections?: number | null;
  sections: {
    components: { key: string; label?: string | null }[];
    type: 'image' | 'content' | 'title';
  }[];
  style: 'carousel' | 'stacked_cards';
}

export interface ProductRecommendationCustomizationSettings {
  /** DYNAMIC_LIST context variable holding products shaped as
   * {id, title, description, image_url?, variants: [{id, title, price,
   * currency_code, details_page_url, image_url?, original_price?}]} */
  products_context_variable?: string | null;
}

export interface BaseJiraServiceManagementCreateRequestSettings {
  include_transcript: boolean;
  is_last_element: boolean;
  issue_id_output_variable?: string | null;
  other_request_fields: { [key: string]: string | number | boolean | null };
  request_type_id: string;
  request_url_output_variable?: string | null;
  service_desk_id: string;
  ticket_id_output_variable?: string | null;
  update_flamethrower_conversation_deflection_status?: boolean;
}

export interface JiraServiceManagementCreateRequestSettings extends BaseJiraServiceManagementCreateRequestSettings {
  // not customizable
  description_context_variable: string;
  email_context_variable: string;
  summary_context_variable: string;
}

export interface GranularJiraServiceManagementCreateRequestSettings extends BaseJiraServiceManagementCreateRequestSettings {
  description: string;
  raise_on_behalf_of: string;
  summary: string;
}

export function isGranularSettings(
  settings:
    | JiraServiceManagementCreateRequestSettings
    | GranularJiraServiceManagementCreateRequestSettings,
): settings is GranularJiraServiceManagementCreateRequestSettings {
  return (
    'raise_on_behalf_of' in settings &&
    'summary' in settings &&
    'description' in settings
  );
}

export interface HubspotTicketCreationSettings {
  custom_fields: Record<string, string | number | boolean> | undefined;
  description_variable: string;
  include_transcript: boolean;
  pipeline?: string | null;
  pipeline_stage?: string | null;
  post_helpdesk_conversation?: boolean;
  priority?: string | null;
  ticket_id_output_variable: string;
  title_variable?: string;
  visitor_email_variable: string;
  visitor_firstname_variable: string;
  visitor_lastname_variable: string;
}

export interface Comm100ChatHandoffSettings {
  custom_variables: Record<string, string | number>;
}

// Type guard function
export function isComm100ChatHandoffSettings(
  obj: unknown,
): obj is Comm100ChatHandoffSettings {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const castObj = obj as { custom_variables?: unknown };
  if (
    typeof castObj.custom_variables !== 'object' ||
    castObj.custom_variables === null
  ) {
    return false;
  }

  return Object.values(castObj.custom_variables).every(
    v => typeof v === 'string' || typeof v === 'number',
  );
}

export interface HubspotChatHandoffSettings {
  contact_summary_field: string | null;
  include_transcript: boolean;
  visitor_email: string | null;
  visitor_first_name: string | null;
  visitor_last_name: string | null;
}

export interface ArticleSuggestionFeedbackSettings {
  button_prompt: string;
  helpful_button_text: string;
  not_helpful_button_text: string;
  show_buttons: boolean;
}

export interface ArticleSuggestionActionSettings extends Partial<BaseArticleConfigurationFields> {
  allow_generative_ai: boolean;
  article_sources: Array<ArticleSource>;
  available_article_sources: Array<ArticleSourceOption>;
  button_prompt: string;
  hand_off_when_not_found?: boolean;
  helpful_button_text?: string;
  input_variable?: string;
  is_last_element: boolean;
  not_helpful_button_text?: string;
  read_article_action: string;
  read_article_action_options: {
    label: string;
    value: ReadArticleActionOptions;
  }[];
  show_buttons?: boolean;
  top_n_articles: number;
}

export interface DynamicArticleSuggestionInitialSettings {
  hand_off_when_not_found: boolean;
  show_buttons: boolean;
  show_not_helpful_text: boolean;
}

export interface SnapEngageChatCustomFieldAndValue {
  custom_field_name: string;
  custom_field_value: string;
}

export interface SnapEngageChatHandoffActionSettings {
  bot_alias_list: string[] | null;
  custom_fields_and_values: SnapEngageChatCustomFieldAndValue[];
  include_transcript: boolean;
  visitor_email_variable: string | null;
  visitor_name_variable: string | null;
  visitor_question_variable: string | null;
}

export interface GorgiasChatHandoffActionSettings {
  include_transcript: boolean;
  question_variable: string;
}

export interface TalkdeskChatHandoffActionSettings {
  email: string | null;
  name: string | null;
  prechat_fields: { name: string; value: string }[] | null;
  question: string;
  transcript_field: string | null;
}

export interface LiveChatChatHandoffActionSettings {
  email_variable: string | null;
  include_transcript: boolean;
  name_variable: string | null;
  question_variable: string;
}

export interface IntercomTicketCreationCustomization {
  contact_id_variable: string | null;
  custom_fields: IntercomCustomFieldAndValue[] | null;
  description_variable: string | null;
  ticket_id_output_variable: string | null;
  ticket_type_id_variable: string | null;
  title_variable: string | null;
}

export interface IntercomCustomFieldAndValue {
  name: string;
  value: string | number | boolean;
}

export interface IntercomCustomFieldAndValuesOptions {
  id: string;
  name: string;
  type: string;
}

export interface IntercomTicketType {
  attributes: IntercomCustomFieldAndValuesOptions[];
  id: string;
  name: string;
  type: string;
}

export interface IntercomFieldsResponse {
  data: IntercomTicketType[];
}
export interface IntercomTicketCreationCustomizationSettings {
  contact_email: string;
  contact_id_variable: string | null;
  contact_name: string;
  context_variables: Record<string, string> | undefined;
  custom_fields: Record<string, string | number | boolean> | undefined;
  description_variable: string | null;
  is_last_element: boolean;
  ticket_id_output_variable: string | null;
  ticket_type_id_variable: string | null;
  title_variable: string | null;
  transcript_visibility?: 'public' | 'internal_note';
  workflow_action_type: string;
}

interface FreshchatHelpdeskConfig {
  api_url: string | null;
  helpdesk: 'freshdesk';
}

export interface ForethoughtLiveChatHandoffActionSettings {
  freshchat_instances_options?: { label: string; value: string }[];
  helpdesk_config: FreshchatHelpdeskConfig | null;
  placeholder_message: string;
  user_id_variable: string | null;
}

export interface SalesforceMessagingHandoffActionSettings {
  available_connectors?: SalesforceAvailableConnector[];
  clear_session_before_handoff: boolean;
  connector_id?: string | null;
  developer_name: string | null;
  hidden_fields: {
    key: string;
    value: string;
  }[];
  include_transcript: boolean;
  jwt?: string | null;
  one_chat: boolean;
  organization_id: string | null;
  question_variable: string;
  scrt_url: string | null;
  visible_fields: {
    editable: boolean;
    field: string;
    value: string;
  }[];
}

export interface FredeshdeskGroup {
  chat_group_id: string;
  id: string;
  name: string;
  routing_logic: string;
  routing_type: string;
}

export interface FreshChatField {
  choices: Array<{
    id: string;
    value: string;
  }>;
  custom: boolean;
  default_value?: string;
  editable: boolean;
  id: string;
  label: string;
  name: string;
  position: number;
  required: boolean;
  type: string;
}
export interface FreshdeskFieldsResponse {
  fields: Array<FreshChatField>;
}

export interface FreshChatCustomField {
  id: string;
  options?: Array<{ name: string; value: string }>;
  title: string;
  type: string;
}

export interface FreshChatCustomFieldAndValue {
  name: string;
  value: string | number;
}

export interface FreshchatChatHandoffActionSettings {
  api_url: string | null;
  custom_fields_and_values: FreshChatCustomFieldAndValue[] | null;
  email_variable: string | null;
  first_name_variable: string | null;
  freshchat_instances_options?: { label: string; value: string }[];
  group: string | null;
  groups?: Array<FredeshdeskGroup>;
  include_transcript: boolean;
  last_name_variable: string | null;
  one_chat: boolean;
  question_variable: string;
  tags: string[] | null;
  transcript_field: string | null;
  user_id_variable: string | null;
}

export interface FreshChatTicketCustomField {
  custom_field_options: Array<{ id: string; value: string }>;
  name: string;
  title: string;
  type: string;
}

export interface ZendeskMessagingHandoffActionSettings {
  auth_token?: string;
  available_ticket_fields: ZendeskTicketCustomField[];
  custom_fields_and_values: Array<ZendeskCustomField>;
  prepend_transcript: boolean;
  question_variable: string;
  ticket_tags: string[];
}

export interface ZendeskCustomField {
  custom_field_id: number;
  custom_field_value: string | number | boolean | Array<string>;
}

export interface ContextVariableMap {
  input_value: string;
  output_value: string;
}

export interface ContextVariableMappingActionSettings {
  input_context_variable: string;
  output_context_variable: string;
  value_map: ContextVariableMap[];
}

export interface ZendeskAttachmentUploadActionSettings {
  required: boolean;
}

export interface ArticleSourceOption {
  available_article_fields: Array<ArticleField>;
  connector_display_name: string;
  connector_id: string;
  doc_type: string;
  emblem_url: string;
}

export interface ArticleField {
  available_field_values: Array<ArticleFieldValues>;
  field_name: string;
  field_type: ElasticSearchDataType | null;
}

export interface ArticleFieldValues {
  name: string;
  value: number | string | boolean | Array<string | number | boolean>;
}

export interface ArticleSource {
  connector_id: string | null;
  doc_type: string | null;
  filter_queries: Array<ArticleSuggestionFilterQuery>;
}

export interface ArticleSuggestionFilterQuery {
  boolean_operator: string | null;
  field: string | null;
  operator: string;
  value: ArticleFieldValues | null;
}

export interface Intent {
  description?: string;
  intent_id: string;
  is_custom: boolean;
  isActive?: boolean;
  phrases: Phrase[];
  title: string;
  workflow_types_enabled?: Array<WorkflowTypes>;
}

// Data passed from component to Redux thunk to add a new Button Step
export interface ButtonStepData {
  isEntryStep?: boolean;
  options: ButtonOption[];
  parentStepId?: string;
  prompt: string;
}

// Data passed from component to Redux thunk to add a new Form Step
export interface FormData {
  formFields: FormField[];
  isEntryStep?: boolean;
  parentStepId?: string;
  prompt: string;
  shouldHidePrepopulatedFields: boolean;
}

export interface CsatTriggerPointData {
  delay: number;
  isEntryStep?: boolean;
  negative_rating_routing_intent?: string;
  parentStepId?: string;
  should_use_delay: boolean;
}

// Data passed from component to Redux thunk to add a new Message Step
export interface MessageStepData {
  isEntryStep?: boolean;
  message: string;
  parentStepId?: string;
  showLinkPreview?: boolean;
}

export interface AggregateData {
  [key: string]: string | number | boolean | null;
}

export interface OrgIdResponse {
  org_id: number;
}

export interface ChannelsResponse {
  channels: Array<string>;
}

export interface MetricDataTypesResponse {
  data_types: {
    [key: string]: string;
  };
}

export interface SolveMetricsRequestQueryParams {
  /**
   * indicates fetching metrics for which channel
   */
  channel?: ConversationChannel;
  /**
   * channel_mode - to support inbound vs outbound on voice
   */
  channel_mode?: ChannelMode;
  /**
   * unix timestamp as string
   */
  from?: string | null;
  /**
   * workflow tags to filter by
   */
  tags?: string[] | null;
  /**
   * unix timestamp as string
   */
  to?: string | null;
}

export interface HandoffUsagesParams {
  intentWorkflowId: string;
}

export interface MetricAggregateResponse {
  aggregate: {
    avg_csat?: number | null;
    cost_savings: {
      change: number;
      contact_support: boolean;
      cost_per_ticket: number;
      cost_savings: number;
    };
    deflection_rate: {
      change: number;
      deflection_rate: number;
    };
    dropoff_breakdown?: {
      dropoff_count: number;
      no_dropoff_count: number;
    };
    relevance_breakdown?: {
      irrelevant_count: number;
      relevant_count: number;
      somewhat_relevant_count: number;
    };
    resolutions: {
      change: number;
      resolutions: number;
    };
    user_queries: {
      change: number;
      user_queries: number;
    };
  };
  last_updated_at: string | null;
  voice_aggregate?: {
    answered_by_user?: number; // outbound, aka eligible calls for outbound
    average_call_duration: number;
    average_deflected_call_duration: number;
    average_non_deflected_call_duration: number;
    average_response_time: number;
    avg_csat: number | null;
    call_duration: number;
    conversations: number;
    cost_savings: {
      change: number;
      contact_support: boolean;
      cost_per_ticket: number;
      cost_savings: number;
    };
    deflected: number;
    deflected_call_duration: number;
    dropoffs?: number; // outbound
    eligible_calls: number; //inbound only
    engaged?: number; // outbound
    engaged_call_duration?: number; // outbound
    non_engaged_call_duration?: number; // outbound
    followup_asks?: number; // outbound
    high_response_time: number;
    ineligible_calls: number;
    low_response_time: number;
    mid_response_time: number;
    negative_sentiments: number;
    neutral_sentiments?: number;
    non_deflected_call_duration: number;
    positive_sentiment_rate: number;
    positive_sentiments: number;
    relevant_count?: number;
    somewhat_relevant_count?: number;
    irrelevant_count?: number;
    total_calls?: number; // outbound
    unanswered?: number; // outbound
    voicemails?: number; // outbound
  };
  web_call_aggregate?: {
    average_call_duration: number;
    average_deflected_call_duration: number;
    average_non_deflected_call_duration: number;
    average_response_time: number;
    avg_csat: number | null;
    call_duration: number;
    conversations: number;
    cost_savings: {
      change: number;
      contact_support: boolean;
      cost_per_ticket: number;
      cost_savings: number;
    };
    deflected: number;
    deflected_call_duration: number;
    eligible_calls: number;
    high_response_time: number;
    ineligible_calls: number;
    irrelevant_count: number;
    low_response_time: number;
    mid_response_time: number;
    negative_sentiments: number;
    neutral_sentiments: number;
    non_deflected_call_duration: number;
    positive_sentiment_rate: number;
    positive_sentiments: number;
    relevant_count: number;
    somewhat_relevant_count: number;
  };
}

export interface MetricBreakdownResponse {
  breakdown: Array<BreakdownData>;
}

export interface WorkflowBuilderLandingPageData {
  aggregate: {
    [key: string]: AggregateData | number | null;
  } & {
    avg_csat?: number | null;
  };
  breakdown: Array<BreakdownData>;
  channels: Array<string>;
  data_types: {
    [key: string]: string;
  };
  org_id: number;
}
export interface DecommissionWorkflowErrorReferencedData {
  id: string;
  intent_definition_id?: string;
  name?: string;
  product?: SolveWidgetProduct;
}
export interface DecommissionWorkflowResponse {
  detail?: {
    message: string;
    referenced_data?: Array<DecommissionWorkflowErrorReferencedData>;
    type?: string;
  };
  success: boolean;
}

interface VoiceConversationBreakdownData {
  average_call_duration?: number; // either inbound or outbound
  call_duration?: number; // inbound only - should refactor to total_call_duration
  num_answered_by_user?: number; // outbound, aka eligible calls for outbound
  num_dropoffs?: number; // outbound
  num_engaged?: number; // outbound
  num_followup_asks?: number; // outbound
  num_unanswered?: number; // outbound
  num_voicemails_left?: number; // outbound
  positive_sentiment_rate?: number; // either inbound or outbound
  total_call_duration?: number; // outbound only - should refactor
}

export type BreakdownData = {
  avg_csat?: number | null;
  deflected: number;
  deflection_rate: number;
  dropoff_breakdown?: DropoffBreakdown;
  has_surge: boolean;
  id: string;
  inquiries: number;
  intent_id: string;
  is_active: boolean;
  is_effective: boolean;
  name: string;
  needs_adjustment: boolean;
  relevance_breakdown?: RelevanceBreakdown;
  topics?: Array<DiscoverTopic>;
  workflow_tags?: string[];
} & VoiceConversationBreakdownData;

export interface WidgetConfigResponse {
  agent_chat_image?: string | null;
  csat_survey_config: {
    is_enabled: boolean;
  };
  display_proactive_chat?: boolean;
  display_proactive_chat_intents?: boolean;
  free_form_intent_detection_enabled: boolean | null;
  dictation_enabled: boolean | null;
  greeting_line: string | null;
  header_image?: string | null;
  help_button_image?: string | null;
  intents?: Array<SimpleIntentData> | null;
  manually_selected_top_intents: Array<string> | null;
  num_top_intents: number | null;
  proactive_chat_greeting?: string;
  quick_feedback_config?: {
    is_enabled: boolean;
  };
  tag_selection_enabled?: boolean;
  theme_color?: string | null;
  top_n_intents?: Array<SimpleIntentData>;
}

export interface SimpleIntentData {
  id: string;
  title: string;
}

export interface CreateWidgetConfigAssetBody {
  content_type: string;
  description?: string;
  file_extension: string;
}

export interface CreateAssetLinkResponse {
  asset_id: string;
  description: string;
  file_name: string;
  public_url: string;
  url: string;
}

export interface Phrase {
  is_modifiable?: boolean;
  phrase_id?: string;
  phrase_text: string;
}

export interface CreateCustomHandoffRequest {
  is_default_handoff: boolean;
  is_handoff: boolean;
  subflow_type?: SubflowType;
}

export interface LanguagesResponse {
  languages: Array<Language>;
}

export interface LanguagesEnabledResponse {
  language_codes: Array<string>;
  modified_date: string | null;
}

export interface LanguagesEnabledRequest {
  channel: ConversationChannel;
  language_codes: Array<string>;
  modified_date: string | null;
}

export interface GetStepTranslationsForOrgRequest {
  language_code: string;
  mode: TranslationsTableMode;
}

export interface GetStepTranslationsForStepRequest {
  intent_workflow_id: string;
  language_code: string;
  step_id: string;
}

export interface GetConfigurationTranslationsForOrgRequest {
  language_code: string;
}

export interface GetStepTranslationsForOrgResponse {
  mode?: TranslationsTableMode;
  translations: StepTranslation[];
}

export interface GetStepTranslationsForStepResponse {
  translations: StepTranslation[];
}

export interface GetConfigurationTranslationsForOrgResponse {
  translations: ConfigurationTranslation[];
}

export interface GetRestoreTranslationResponse {
  index: number;
  translation: StepTranslation | ConfigurationTranslation;
}

export interface ConfigurationTranslation {
  brand_id: string | null;
  bucket_id: string;
  configuration_id: string;
  configuration_path?: string;
  display_name: string;
  intent_id?: string | null;
  is_override: boolean;
  language_code: string;
  modified_date: string;
  org_id: number;
  source_text: string;
  solve_widget_product?: SolveWidgetProduct | null;
  target_text: string;
  translation_request_id: string;
}

export interface StepTranslation {
  bucket_id: string;
  display_name: string;
  element_path: string;
  intent_id?: string | null;
  intent_workflow_id: string;
  intent_workflow_version: number;
  is_override: boolean;
  language_code: string;
  modified_date: string;
  org_id: number;
  parent_workflow_id: string | null;
  product?: SolveWidgetProduct | null;
  source_text: string;
  step_id: string;
  target_text: string;
  translation_request_id: string;
  translation_type: string;
  workflow_is_draft: boolean;
}

export function isStepTranslation(
  object: StepTranslation | ConfigurationTranslation,
): object is StepTranslation {
  return 'step_id' in object;
}

export interface OverrideTranslationRequest {
  element_path: string;
  intent_workflow_id: string;
  modified_date: string;
  step_id: string;
  target_text: string;
}

export interface OverrideConfigurationTranslationRequest {
  brand_id: string | null;
  configuration_id: string;
  configuration_path?: string;
  intent_id?: string | null;
  modified_date: string;
  solve_widget_product?: SolveWidgetProduct | null;
  source_text?: string;
  target_text: string;
}

export interface Language {
  code: string;
  display_name: string;
  language_id: string;
}

export interface SuggestedIntent {
  description?: string | null;
  expected_coverage?: number | null;
  intent_id: string;
  intent_type: string;
  phrases?: Phrase[] | null;
  title: string;
}

export interface SuggestedIntentsResponse {
  suggested_intents: SuggestedIntent[];
}

export interface IntentData {
  active_workflow_types: WorkflowTypes[];
  api_is_default_handoff: boolean;
  api_is_handoff: boolean;
  api_workflow_id: string | null;
  api_workflow_is_autoflow: boolean;
  channels: ConversationChannel[];
  email_autopilot_enabled: boolean;
  email_human_in_the_loop_enabled: boolean;
  /** Agent Builder folder IDs this intent workflow belongs to. Empty means Unfiled. */
  folder_ids?: string[];
  has_pending_improvement?: boolean;
  inquiry_count_per_channel: {
    api?: number;
    email: number;
    playbook?: number;
    slack?: number;
    voice?: number;
    web_call?: number;
    widget: number;
  };
  intent_definition_id: string;
  intent_name: string;
  intent_workflow_id: string;
  interactive_email_workflow_id: string | null;
  interactive_email_workflow_is_autoflow: boolean;
  is_all_tags: boolean;
  is_autoflow: boolean;
  is_default_handoff: boolean;
  /** False for pinned system agents that cannot be moved to folders. */
  is_folderable?: boolean;
  is_handoff: boolean;
  playbook_workflow_id: string | null;
  slack_is_default_handoff: boolean;
  slack_is_handoff: boolean;
  slack_workflow_id: string | null;
  slack_workflow_is_autoflow: boolean;
  subflow_type: SubflowType;
  voice_workflow_id: string | null;
  web_call_workflow_id: string | null;
  workflow_tags: string[];
}

export interface IntentsResponse {
  intents: IntentData[];
}

export interface CanvasResponse {
  canvas: Canvas;
  canvas_action_id: string | null;
  handoff_configuration?: HandoffConfiguration;
  invalid_go_to_steps: string[];
  parent_provided_context_variables?: string[] | null;
  step_id: string;
  undefined_context_variables_in_step?: Record<string, string[]> | null;
  unsupported_steps: string[];
}

export type ToolType =
  | 'demo'
  | 'api_call'
  | 'hand_off'
  | 'intent_routing'
  | 'template_api_call'
  | 'system_built_in'
  | 'custom_event'
  | 'workflow'
  | 'open_side_conversation'
  | 'mcp'
  | 'zendesk_flowstate_action'
  | 'browser_agent'
  | 'wait';

export interface AddAutoflowToolRequest {
  tool_id: string;
  tool_type: ToolType;
}

export interface DeleteAutoflowToolRequest {
  tool_id: string;
  tool_type: ToolType;
}

export interface PatchAutoflowCVRequest {
  context_variable_id: string;
}

export interface PatchAutoflowVersionHistoryRequest {
  description: string;
  is_draft: boolean;
  version_name: string;
}

export interface AutoflowTool {
  configuration_fields: Partial<{
    available_article_sources: Array<ArticleSourceOption>;
    available_groups: ZendeskGroup[];
    available_priorities: Array<{ label: string; value: string }>;
    available_statuses: Array<{ label: string; value: string }>;
    available_teams: PylonTicketCreationCustomizationSettings['available_teams'];
    available_ticket_brands: Array<string>;
    available_ticket_fields: ZendeskTicketCustomField[];
    available_ticket_types: Array<IntercomTicketType>;
    available_users: PylonTicketCreationCustomizationSettings['available_users'];
    pylon_custom_fields: PylonTicketCreationCustomizationSettings['pylon_custom_fields'];
    custom_ticket_properties: HubspotTicketProperties['custom_ticket_properties'];
    event_configuration_id: string;
    parent_autoflow_intent_workflow_id?: string;
    priority_options: HubspotTicketProperties['priority_options'];
    properties: EventProperty[];
  }>;
  is_recommended?: boolean;
  tool_id: string;
  tool_name: string;
  tool_type: ToolType;
}

export interface UsableTool extends AutoflowTool {
  isActive: boolean;
  // True for a go-to/workflow route that is still configured in the policy but
  // whose target intent has dropped out of the API-derived available set (e.g.
  // it was deactivated for this channel). Such tools are surfaced so the usage
  // can be removed, but are excluded from the "add" menus so no new references
  // can be created. Cleared automatically once the target is reactivated.
  isRemovalOnly?: boolean;
  toolDefinition?: ToolDefinitionResponse;
}

export interface AutoflowToolActionFields {
  connectorName?: string;
  description: string;
  dynamicListOutputVariables?: ContextVariable[];
  inputVariables: ContextVariable[];
  isDeleted?: boolean;
  isDisabled: boolean;
  outputVariables: ContextVariable[];
  systemBuiltInToolExamplePolicy?: string;
  systemBuiltInToolInput?: string[];
}

export interface AutoflowToolResponse {
  tools: AutoflowTool[];
}

export interface BuiltInToolOutputParam {
  description: string;
  name: string;
  type: 'string' | 'success_fail';
}

export interface BuiltInToolSchema {
  description: string;
  output_params: BuiltInToolOutputParam[];
  tool_id: string;
  tool_name: string;
}

export interface BuiltInToolSchemasResponse {
  tools: BuiltInToolSchema[];
}

export interface ToolDefinitionResponse {
  context_variable_output_mapping?: Record<string, string>;
  description: string;
  name: string;
  tool_version?: number | null;
  tool_fields?: Partial<{
    api_access_key?: string;
    api_access_secret?: string;
    article_sources: Array<ArticleSource>;
    cv_ids?: string[];
    delay_seconds: number;
    interruptible?: boolean;
    play_hold_music?: boolean;
    dynamics_365_case_creation_settings: Dynamics365CaseCreationSettings;
    event_configuration_id?: string | null;
    freshdesk_ticket_creation_settings: FreshdeskTicketCreationSettings;
    gorgias_ticket_creation_settings: GorgiasTicketCreationCustomizationSettings;
    pylon_ticket_creation_settings: PylonTicketCreationCustomizationSettings;
    hubspot_helpdesk_ticket_creation_settings: HubspotTicketCreationSettings;
    hubspot_ticket_creation_settings: HubspotTicketCreationSettings;
    intercom_ticket_creation_settings: IntercomTicketCreationSettings;
    parent_autoflow_intent_workflow_id?: string | null;
    properties?: EventProperty[] | null;
    salesforce_case_creation_settings: SalesforceCaseCreationSettings;
    // ServiceNow autoflow create-ticket tools (create_servicenow_case /
    // create_servicenow_incident). Optional via the outer Partial<>.
    servicenow_case_creation_settings: ServicenowCaseCreationSettings;
    servicenow_case_incident_creation_settings: ServicenowCaseIncidentCreationSettings;
    sip_uri?: string | null;
    should_mark_conversation_as_not_deflected?: boolean;
    sip_header_cv_ids?: string[];
    skip_initial_response?: boolean;
    // twilio_pay tool_fields. Must match the keys read by the runtime
    // executor in forethought-server/app/solve_voice/tools.py (the
    // create_twilio_pay_executor `tool_fields` argument).
    //
    // `payment_description` is intentionally NOT named `description` —
    // ToolDefinitionResponse already has a top-level `description` field
    // (the tool's own description), and shadowing it inside tool_fields
    // makes greps and refactors error-prone.
    payment_connector_unique_name?: string;
    currency?: string;
    payment_description?: string;
    // Provider-agnostic CV bindings — the executor dereferences these via
    // conversation_context[cv_id]. account_holder_name_cv_id is optional.
    processor_type?: string;
    processor_account_id_cv_id?: string;
    processor_auth_token_cv_id?: string;
    external_reference_cv_id?: string;
    charge_amount_cv_id?: string;
    account_holder_name_cv_id?: string;
    // zendesk_sip_handoff fields (flat, matching sip_handoff pattern)
    use_phone_number_for_ticket_requester?: boolean;
    additional_tags?: string[];
    custom_fields?: ZendeskCustomFieldAndValue[];
    failure_message?: string | null;
    group_id?: number | null;
    requester_id_cv_id?: string | null;
    ticket_brand?: string | null;
    ticket_form_id_cv_id?: string | null;
    ticket_status?: string | null;
    visitor_email_cv_id?: string | null;
    visitor_name_cv_id?: string | null;
    zendesk_ticket_creation_settings: ZendeskTicketCreationSettings;
  }>;
  tool_id: string;
  tool_type: ToolType;
}

export interface ZendeskTicketCreationSettings {
  brand_name: string | null;
  // Pins the tool to a specific Zendesk connector; null/undefined uses the default.
  connector_id?: string | null;
  custom_fields_and_values?: ZendeskCustomFieldAndValue[];
  use_phone_number_for_ticket_requester?: boolean;
  ticket_custom_fields?: ZendeskTicketCustomField[];
  ticket_id_output_variable: string | null;
  ticket_status: string | null;
  ticket_tags: string[];
  update_flamethrower_conversation_deflection_status: boolean;
  visitor_email_variable: string | null;
  visitor_name_variable: string | null;
}

export interface ZendeskSipHandoffSettings {
  use_phone_number_for_ticket_requester?: boolean;
  additional_tags: string[];
  custom_fields: ZendeskCustomFieldAndValue[];
  failure_message: string | null;
  group_id: number | null;
  requester_id_cv_id: string | null;
  sip_uri: string;
  ticket_brand: string | null;
  ticket_form_id_cv_id: string | null;
  ticket_status: string | null;
  visitor_email_cv_id: string | null;
  visitor_name_cv_id: string | null;
}

export interface FreshdeskTicketCreationSettings {
  custom_fields: { [key: string]: string | number | boolean } | null;
  ticket_group: string | number | null;
  ticket_id_output_variable: string | null;
  ticket_priority: string | number | null;
  ticket_product: string | number | null;
  ticket_status: string | null | number;
  ticket_tags: Array<string>;
  ticket_type: string | null;
  update_flamethrower_conversation_deflection_status: boolean;
  user_company: string | number | null;
  visitor_email_variable: string | null;
  visitor_name_variable: string | null;
}

export interface SalesforceCaseCreationSettings {
  case_number_output_variable: string | null;
  // Pins the tool to a specific Salesforce connector; null/undefined uses the default.
  connector_id?: string | null;
  create_email_message: boolean | null;
  transcript_format?: 'html' | 'plain_text';
  ticket_fields: Array<{
    id: string;
    is_custom?: boolean;
    value: string;
  }>;
  ticket_id_output_variable: string | null;
  update_flamethrower_conversation_deflection_status: boolean;
  visitor_email_variable: string | null;
  visitor_name_variable: string | null;
}

export interface IntercomTicketCreationSettings {
  ticket_id_output_variable: string | null;
  ticket_type: string | null;
  visitor_email_variable: string | null;
  visitor_name_variable: string | null;
}

export interface AutoflowVersionHistory {
  description: string;
  intent_workflow_id: string;
  is_draft: boolean;
  modified_date: string;
  updated_by: string;
  version: number;
  version_name: string;
}

export interface AutoflowVersionHistorySummary {
  ab_test_variants?: ABTestVariantInfo[];
  intent_workflow_id: string;
  is_published: boolean;
  last_updated_at: string;
  updated_by: string;
  version: number;
  version_name: string;
  version_type: WorkflowVersionType;
}

/**
 * The SAVED autoflow version carrying a started review of a recommendation.
 * `version` / `version_type` key the editor's version navigation on resume,
 * `version_name` labels the resume link, and `autoflow_id` keys the PolicyDiff
 * endpoints.
 */
export interface InProgressReview {
  autoflow_id: string;
  version: number;
  version_name: string | null;
  version_type: WorkflowVersionType;
}

/**
 * A GEPA recommendation open for review on a workflow, surfaced on the autoflow
 * GET so the FE can drive the entry/resume banner without a second query.
 * `source_recommendation_id` is the recommendation to surface; `summary` and
 * `improvement_bytes` are its reviewer-facing highlights for the entry card;
 * `in_progress` is the started review to resume, or null for a fresh
 * recommendation not yet opened for review.
 */
export interface AvailableRecommendation {
  /** When the recommendation was generated (ISO); the review version is named
   *  after this date rather than today. Null on legacy rows. */
  generated_at: string | null;
  /** Short reviewer-facing improvement highlights for the entry card. */
  improvement_bytes: string[];
  in_progress: InProgressReview | null;
  /**
   * Provenance counts for the run's training data, for the card's "From …"
   * line. All null (or absent pre-deploy) on runs that recorded no
   * conversation ids — render the line without counts then.
   */
  source_conversations?: number | null;
  source_human_reviews?: number | null;
  source_recommendation_id: string;
  source_tickets?: number | null;
  /** Reviewer-facing what-changed summary, or null. */
  summary: string | null;
}

export interface AutoflowIntentWorkflowResponse {
  /**
   * Set when a GEPA recommendation is open for review on this workflow; null
   * when there is none. Drives the entry vs resume banner — see
   * AvailableRecommendation.
   */
  available_recommendation?: AvailableRecommendation | null;
  context_variables: string[];
  intent_workflow_id: string;
  is_draft: boolean;
  is_duplicated_pending_customization: boolean;
  is_published: boolean;
  last_updated_at: string | null;
  policy_description: string;
  status: 'enabled' | 'disabled';
  tool_definitions: ToolDefinitionResponse[];
  unsaved_changes: boolean;
  version: number;
  version_name: string;
  version_type: WorkflowVersionType;
}

export interface TemplateAutoflowIntentWorkflow extends AutoflowIntentWorkflowResponse {
  instruction?: string;
  name: string;
  sample_user_query: string;
  template_intent_phrases: string[];
}

export interface RestorationRequirements {
  deleted_actions: Array<{
    action_id: string;
    action_name: string;
    action_type: string | null;
    can_restore: boolean;
    snapshot_version: number;
  }>;
  deleted_child_workflows: Array<{
    can_restore: boolean;
    intent_definition_id: string;
    snapshot_version: number | null;
    workflow_name: string;
  }>;
  deleted_cvs: Array<{
    context_variable_id: string;
    display_name: string;
    matching_cv_id: string | null;
    type: string | null;
    will_create_new: boolean;
  }>;
  deleted_event_configs: Array<{
    event_configuration_id: string;
    name: string;
    will_rename: boolean;
  }>;
  modified_actions: Array<{
    action_id: string;
    action_name: string;
    action_type: string | null;
    can_restore: boolean;
    current_version: number;
    snapshot_version: number;
  }>;
  needs_restoration: boolean;
  summary: {
    deleted_action_count: number;
    deleted_child_workflow_count: number;
    deleted_cv_count: number;
    deleted_event_config_count: number;
    modified_action_count: number;
  };
}

export interface DeletedBuildingBlockResponse {
  canvas_action_id_to_action_component: Record<string, ActionStepList>;
  entry_step_id: string | null;
  intent_definition_id: string;
  intent_workflow_id: string;
  step_map: Record<string, Step>;
  version: number;
}

export interface WorkflowVersionsResponse {
  versions: WorkflowVersion[];
}

export interface WorkflowVersion {
  is_draft: boolean;
  modified_date: string | null;
  version: number;
}

export interface CanvasIntentsResponse {
  intents: Array<{
    intent_name: string;
    intent_workflow_id: string;
    is_active: boolean;
  }>;
}

export interface HandoffConfigurationsResponse {
  configurations: HandoffConfiguration[];
}

export interface IntentsResponse {
  intents: IntentData[];
}

export interface Canvas {
  activated_date: string | null;
  api_workflow_id: string | null;
  canvas_action_id_to_action_component: Record<
    Action['action_id'],
    ActionStepList
  >;
  created_date: string | null;
  deactivated_date: string | null;
  entry_step_id: string | null;
  intent_id: string;
  intent_title: string;
  intent_workflow_id: string;
  is_active: boolean;
  is_all_tags: boolean;
  is_draft: boolean;
  is_duplicated_pending_customization: boolean;
  modified_date: string | null;
  org_id: number;
  playbook_workflow_id: string | null;
  policy_description?: string | null;
  preview_api_token: string | null;
  slack_workflow_id: string | null;
  step_map: Record<string, Step>;
  unsaved_changes: boolean;
  version: number;
  voice_workflow_id: string | null;
  web_call_workflow_id: string | null;
  workflow_recommendation: WorkflowRecommendation | null;
  workflow_size_status?: 'stable' | 'warning' | 'error';
  workflow_tags: Array<string>;
}

export interface WorkflowRecommendation {
  conversation_ids: string[];
  problematic_convo_str: string;
  recommendation: string;
  summary: string;
}

export interface Step {
  condition_name: string | null;
  step_fields:
    | TextMessageFields
    | ButtonsStepFields
    | FormStepFields
    | GoToComponentStepFields
    | GoToIntentStepFields
    | GoToIntentEmailStepFields
    | ArticleStepFields
    | ConditionStepFields
    | ZendeskTicketCreationStepFields
    | ZendeskChatHandoffStepFields
    | SalesforceCaseCreationStepFields
    | SalesforceChatHandoffStepFields
    | ImageStepFields
    | EmbedStepFields
    | CsatTriggerPointStepFields
    | AttachmentAnalysisUploadStepFields
    | CodeBlockStepFields
    | MiniAutoflowPolicyStepFields
    | SpeechStepFields;
  step_type: StepTypes;
  transitions: Transition[];
}

export type Nullable<T> = { [P in keyof T]?: T[P] | null };
export interface BaseTransition {
  condition_expression: ConditionExpressions;
  step_id: string;
  transition_id: string;
  uses_mapped_fields?: boolean;
}
export type Transition = Nullable<BaseTransition>;

export interface TextMessageFields {
  message: string;
  show_link_preview?: boolean;
}

export interface ArticleStepFields {
  doc_type: string;
  document_id: string;
  document_title: string;
  read_article_action: string;
  standardized_document_id: string;
}

export interface ImageStepFields {
  images: {
    description: string;
    url: string;
  }[];
}

export interface EmbedStepFields {
  embed_url: string;
}

export interface ButtonsStepFields {
  hide_free_form_query?: boolean;
  multi_select: boolean;
  options: ButtonOption[];
  output_variable: string;
  prompt: string;
}

export interface MiniAutoflowPolicyTransition {
  condition: string;
  transition_id: string;
}

export interface MiniAutoflowPolicyStepFields {
  allow_longer_user_pauses: boolean;
  allowed_tool_ids: string[];
  context_variables: string[];
  max_node_turns?: number | null;
  output_context_variable: string;
  task: string;
  transitions: MiniAutoflowPolicyTransition[];
}

export interface ConditionStepFields {
  condition_name: string;
}

export interface CsatTriggerPointStepFields {
  delay: number;
  negative_rating_routing_intent?: string;
  should_use_delay: boolean;
}

export interface ButtonOption {
  display_name: string;
  transition_id?: string | null;
  url_config?: UrlConfig;
  value: string;
}

export interface FormStepFields {
  form_fields: FormField[];
  hide_prepopulated_fields: boolean;
  is_skippable?: boolean;
  pre_populate_fields: boolean;
  prompt: string;
}

export interface FormField {
  dropdown_options?: DropdownOptionsType[];
  dynamic_list_option_mapping?: string;
  input_type: string;
  output_variable: string;
  placeholder: string;
  prepopulated_field?: string;
  required?: boolean;
}

type CustomFieldValue = boolean | number | string | string[];

export type ContextVariableValue = boolean | number | string | string[];

type CustomFieldPair = [string, CustomFieldValue];

export interface ZendeskTicketCreationStepFields {
  additional_tags: string[];
  brand_name: string | null;
  custom_fields: CustomFieldPair[][];
  include_attachments: boolean;
  include_transcript: boolean;
  update_conversation_status: boolean;
  update_flamethrower_conversation_deflection_status: boolean;
  visitor_email_variable: string | null;
  visitor_name_variable: string | null;
  visitor_question_variable: string | null;
  visitor_subject_formattable_message: string | null;
  visitor_subject_variable: string | null;
}

export interface ZendeskChatHandoffStepFields {
  chat_tags: string[] | null;
  department: string | null;
  ignore_agent_availability: boolean;
  update_flamethrower_conversation_deflection_status: boolean;
  visitor_email_variable: string;
  visitor_name_variable: string;
  visitor_question_variable: string;
}

export interface SalesforceCaseCreationStepFields {
  custom_fields: {};
  standard_fields?: Record<string, string>;
  transcript_field: string;
  transcript_format?: 'html' | 'plain_text';
  update_flamethrower_conversation_deflection_status: boolean;
  visitor_email_variable: string;
  visitor_name_variable: string;
  visitor_question_variable: string;
}

export interface SalesforceChatHandoffStepFields {
  extra_prechat_form_details: string[][][];
  salesforce_chat_config: string[][];
  update_flamethrower_conversation_deflection_status: boolean;
  visitor_email_variable: string;
  visitor_name_variable: string;
  visitor_question_variable: string;
}

export interface UrlConfig {
  base_url: string;
  query_parameters: Record<string, string>;
}

export type ContextVariablesStep = {
  defined_context_variables: string[];
  step_output_variables: string[];
};

export type ActionStepList = {
  action_id: Action['action_id'];
  config_fields?: Record<string, unknown>;
  entry_step_id: string;
  is_last_element: boolean;
  steps: string[];
  transition_branches?: Array<ActionTransitionBranch>;
};

export type ActionTransitionBranch = {
  display_name: string;
  parent_step_id: string;
};

export type GoToIntentStepFields = {
  intent_workflow_id: string;
};

export type GoToComponentStepFields = {
  target_intent_workflow_id: string;
};

export type GoToIntentEmailStepFields = {
  intent_definition_id: string;
};

export interface SpeechStepFields {
  text: string;
}

export interface PreviewTokenReponse {
  preview_api_token: string;
}

export interface SolveApiKeyResponse {
  api_key: string;
}

export interface SlackInstallationDetail {
  app_id: string | null;
  team_id: string | null;
  team_name: string | null;
  enterprise_id: string | null;
  enterprise_name: string | null;
  is_enterprise_install: boolean | null;
  bot_scopes: string[] | null;
  installed_at: number | null;
  is_custom_app: boolean;
}

export interface SlackInstallationResponse {
  has_active_installation: boolean;
  installations: SlackInstallationDetail[];
}

export interface DeflectionConfig {
  knowledge_config: {
    doc_types: string[];
  };
}

interface EsDocumentOverview {
  doc_count: number | null;
  doc_type: string;
}

export interface SolveApiConfigResponse {
  config: DeflectionConfig | null;
  document_overview: EsDocumentOverview[];
}

export interface WidgetApiKeyResponse {
  api_key: string;
}

export interface WorkflowBuilderSettingsResponse {
  created_date: string;
  global_context_variables: GlobalContextVariable[];
  org_id: number;
}

export interface HandoffIntentWorkflowUsage {
  intent_definition_id: string;
  intent_title: string;
  intent_workflow_id: string;
}

export interface HandoffUsagesResponse {
  usages: HandoffIntentWorkflowUsage[];
}

export type RestartConversationStepFields = {
  restart_message: string;
  show_greeting_message: boolean;
  show_top_n_intents: boolean;
};

export interface ModelTrainingResponse {
  is_model_training: boolean;
}

export type Article = {
  document_id: string;
  link?: string;
  permission: string;
  preview: string;
  source_type: string;
  standardized_document_id: string;
  title: string;
};

interface MacroSearchResult extends Article {
  html: string;
}

export type ArticlesResponse = {
  articles: Article[];
  is_last_page: boolean;
  page: number;
  query: string;
  total_hits: number;
};

export interface MacroSearchResponse {
  is_last_page: boolean;
  macros: MacroSearchResult[];
  page: number;
  query: string;
  total_hits: number;
}

export interface WhitelistedDomainsResponse {
  allow_all_domains: boolean;
  whitelist: string[] | null;
}

export interface WhitelistedDomainsRequest {
  allow_all_domains?: boolean;
  whitelist: string[] | null;
}

export interface StepMetricsResponse {
  csat: {
    average_score: number;
  };
  deflection_rate: {
    count: number;
    total_count: number;
  };
  dropoff: {
    count: number;
    total_count: number;
  };
  step_is_modified: boolean;
  step_last_modified_at: string;
  traffic: {
    count: number;
    total_count: number;
  };
}

export interface StepMetricsOfABranch {
  button_display_name: string;
  button_index: number;
  button_value: string;
  csat: {
    average_score: number;
  };
  deflection_rate: {
    count: number;
    total_count: number;
  };
  traffic: {
    count: number;
    total_count: number;
  };
}

export interface BranchingStepMetricsResponse {
  buttons: StepMetricsOfABranch[];
  dropoff: {
    count: number;
    total_count: number;
  };
  step_is_modified: boolean;
  step_last_modified_at: string;
}

export interface ConversationMetrics {
  date_created: string;
  first_input_line: string;
  intent_names: Array<string>;
  is_deflected: boolean;
  is_need_review: boolean;
  workflow_tags: Array<string>;
}
export type KustomerHandoffCustomAttribute = {
  field: string;
  value: number | boolean | string;
};

export type KustomerResourceCustomAttribute = {
  display_name: string;
  id: string;
  options: string[];
  type: string;
};

export interface KustomerChatHandoffCustomization {
  agent_chat_handoff_settings: {
    assistant_id?: string | null;
    custom_attributes: KustomerHandoffCustomAttribute[];
    handoff_form_prompt: string;
    kustomer_custom_attributes: KustomerResourceCustomAttribute[];
    transcript_field?: string | null;
    upload_files_attached?: boolean | null;
    write_transcript_as_customer_facing_message?: boolean;
    write_transcript_to_note: boolean;
  };
  email: string | null;
  jwt: string | null;
  name: string | null;
  one_chat: boolean;
  question: string | null;
}

export interface KustomerApp {
  id: string;
  name: string;
}

export interface KustomerConversationCreationCustomization {
  app?: string;
  channel: string;
  conversation_creation_settings: {
    confirmation_message: string;
    custom_attributes: KustomerHandoffCustomAttribute[];
    handoff_form_question_message: string;
    kustomer_available_apps?: Array<KustomerApp>;
    kustomer_conversation_channels?: Array<string>;
    kustomer_custom_attributes: KustomerResourceCustomAttribute[];
    subject: string | null;
    transcript_field?: string | null;
    upload_files_attached?: boolean | null;
    write_transcript_to_note: boolean;
  };
  email: string | null;
  name: string | null;
  phone_number?: string;
  question: string | null;
  tags?: string[];
}

export interface KustomerAttributeField {
  field: string;
  type: string;
  value: number | boolean | string;
}

export interface KustomerHandoffCustomization
  extends
    KustomerChatHandoffCustomization,
    KustomerConversationCreationCustomization {
  include_agent_chat_handoff: boolean;
  include_conversation_creation_handoff: boolean;
}

export type LandingPageHelperAPIResponse =
  | OrgIdResponse
  | ChannelsResponse
  | MetricDataTypesResponse
  | MetricAggregateResponse
  | MetricBreakdownResponse
  | Array<DiscoverAutomationsByTypeResponse>;

export interface SolveEmailMetricsBase {
  /**
   * Number of email conversations where we sent a response.
   */
  attempted: number;
  /**
   * estimated cost per ticket.
   */
  cost_per_ticket: number;
  /**
   * cost per ticket * self_served
   */
  cost_savings: number;
  /**
   * Number of email conversations that were created.
   */
  inquiries: number;
  /**
   * when the table was last updated
   */
  last_updated_date: string;
  /**
   * Number of email conversations where we had an opportunity to send a response.
   */
  potential_attempts: number;
  /**
   * self_served / inquiries.
   */
  received_deflection_rate: number;
  /**
   * self_served / attempts.
   */
  responded_deflection_rate: number;
  /**
   * attempts / potential_attempts.
   */
  response_rate: number;
  /**
   * Number of email conversations that were self served.
   */
  self_served: number;
  /**
   * Number of email conversations classified as contained resolutions.
   */
  contained_resolutions: number;
  /**
   * Number of email conversations answered by AI whose resolution has not
   * been classified yet.
   */
  pending_resolutions: number;
  /**
   * verified_resolutions + contained_resolutions.
   */
  total_resolutions: number;
  /**
   * Number of email conversations classified as verified resolutions.
   */
  verified_resolutions: number;
  /**
   * self_served / inquiries
   */
  self_served_rate: number;
}

export interface EmailConversationMetricBreakdownRow extends SolveEmailMetricsBase {
  /**
   * intent definition id - to match metrics to intents
   */
  intent_definition_id: string;
}

export interface EmailConversationMetricBreakdown {
  breakdown: EmailConversationMetricBreakdownRow[];
}

export interface OnboardingFlagsResponse {
  is_bot_activated: boolean;
  is_handoff_onboarding_passed: boolean;
  is_knowledge_onboarding_passed: boolean;
  is_onboarding_finished: boolean;
  is_widget_onboarding_passed: boolean;
}

export type UpdateOnboardingFlags =
  | 'is_theme_visited'
  | 'is_handoff_visited'
  | 'is_knowledge_visited';

export type UpdateOnboardingFlagsRequest = RequireExactlyOne<
  Record<UpdateOnboardingFlags, true>
>;

export type GenerateResponseActionType = 'rephrase' | 'extend' | 'shorten';

export interface GenerateResponseRequest {
  action: GenerateResponseActionType;
  text: string;
}

/**
 * A single, uncollapsed reference to an intent from a workflow that go-tos it.
 * Preserves the version-suffixed `source_id` (`:draft` / `:live` / `:saved:{n}`)
 * and `product` so the dashboard can deeplink to the exact version.
 *
 * Currently shaped identically to `ActionReference`, but kept as its own
 * declaration (not aliased) because it mirrors an *independent* backend model
 * (`solve_models.GoToIntentReference`) that can diverge from the action one. The
 * shared `WorkflowReferenceLike` in `actionReferences.ts` is the contract the
 * reusable helpers (`buildActionReferenceUrl`, etc.) are typed against, so both
 * reference types stay usable with them without being locked together.
 */
export interface GoToIntentReference {
  intent_definition_id: string | null;
  parent_autoflow_id: string | null;
  product: string | null;
  source_entity_type: string | null;
  source_id: string;
  source_type: string | null;
}

export interface GoToIntentUsage {
  channels_using_intent_workflow: string[];
  intent_workflow_id: string;
  intent_workflow_ids_using_intent_workflow: string[];
  // Per-reference detail (uncollapsed) for the version sub-dropdown. Optional for
  // backward compatibility with backends that predate this field.
  references?: GoToIntentReference[];
}

export interface GoToIntentUsagesResponse {
  usages: GoToIntentUsage[];
}

export interface ContextVariablesResponse {
  context_variables: ContextVariable[];
  runtime_system_context_variables: ContextVariable[];
  template_context_variables: ContextVariable[];
  usable_system_context_variables: ContextVariable[];
}

interface MentionFormat {
  prefix: string;
  suffix: string;
}

interface ProductMentionOption {
  image?: string;
  label: string;
  value: string; // Optional property
}

export interface ProductMentionField {
  mention_format: MentionFormat;
  mention_trigger: string;
  name: string;
  options: ProductMentionOption[];
}

export interface IntercomLiveChatActionSettings {
  conversation_attributes: Array<{
    name: string;
    value: string;
  }> | null;
  transcript_visibility?: 'public' | 'internal_note';
  user_email: string | null;
  user_id: string | null;
  user_name: string | null;
}

export interface IntercomConversationAttribute {
  data_type: string;
  name: string;
  options?: Array<string>;
  value: string;
}

export interface IntercomLiveChatHandoffCustomization {
  conversation_attributes: Array<IntercomConversationAttribute>;
}

export interface ActiveIntentsResponse {
  active_intents: Array<{
    id: string;
    title: string;
  }>;
}

export interface ConversationOauthSignInCustomizationSettings {
  conversation_oauth_client_id: string;
  disable_other_inputs: boolean;
  error_message: string;
  prompt: string;
  sign_in_button_label: string;
  success_message: string;
}

export interface GladlyChatHandoffSettings {
  visitor_email: string;
  visitor_name: string;
  visitor_question: string;
}

export interface Dynamics365CaseCreationSettings {
  account_id?: string | null;
  additional_fields?: Record<string, string | number | boolean> | null;
  case_origin_code?: number | null;
  customer_contact_id?: string | null;
  description: string;
  owner_team_id?: string | null;
  owner_user_id?: string | null;
  state_code?: number | null;
  status_code?: number | null;
  subject_id?: string | null;
  ticket_number_output_variable?: string | null;
  ticket_output_variable?: string | null;
  title: string;
}

export interface ServicenowTicketField {
  field_options: Array<{ name: string; value: string }>;
  id: string;
  is_custom: boolean;
  title: string;
  type: string;
}

export interface ServicenowFieldAndValue {
  id: string;
  is_custom: boolean;
  value: string;
}

export interface ServicenowTicketCreationConfig {
  fields_and_values: ServicenowFieldAndValue[];
  object_type: 'incident' | 'sn_customerservice_case';
  ticket_fields?: ServicenowTicketField[];
  ticket_title: string | null;
  update_flamethrower_conversation_deflection_status: boolean;
}

export interface ServicenowCaseIncidentCreationSettings {
  account_id_variable: string;
  include_transcript: boolean;
  question: string;
  ticket_creation_settings: {
    ticket_creation_config: ServicenowTicketCreationConfig;
  };
  ticket_id_output_variable: string | null;
}

export interface ServicenowCaseCreationSettings {
  account_id_variable: string;
  include_transcript: boolean;
  question: string;
  ticket_creation_settings: {
    ticket_creation_config: ServicenowTicketCreationConfig;
  };
  ticket_id_output_variable: string | null;
}

export type GenerationSource =
  | 'autoflow_instructions'
  | 'conversations'
  | 'both';

export interface GenerateTestCaseRequest {
  conversation_ids?: string[];
  intent_id: string;
  num_conversations?: number;
  product: SolveWidgetProduct;
  source: GenerationSource;
}

export interface QuiqChatHandoffActionSettings {
  custom_fields: Record<string, string | number | boolean> | null;
  transcript_field: string | null;
  update_flamethrower_conversation_deflection_status: boolean;
}

export interface CallCorpChatHandoffActionSettings {
  visitor_name: string | null;
  visitor_email: string | null;
  user_data_variables: Record<string, string | number | boolean> | null;
  update_flamethrower_conversation_deflection_status: boolean;
}

export type FlowDiagramNodeKind = 'step' | 'condition' | 'action';

export interface FlowDiagramClassicStepTarget {
  path_step_id?: string | null;
  kind: 'classic_step';
  policy_flow_id: string;
  intent_workflow_id: string;
  workflow_version: number;
  step_id: string;
}

export interface FlowDiagramClassicBlock {
  id: string;
  label: string;
  policy_flow_id: string | null;
  reference_flow_ids: string[];
  intent_workflow_id: string | null;
  workflow_version: number | null;
  nodes: FlowDiagramNode[];
  unavailable: boolean;
  truncated: boolean;
  source_fingerprint: string;
}

export interface FlowDiagramNode {
  classic_block_ids?: string[];
  is_otherwise?: boolean;
  navigation_target?: FlowDiagramClassicStepTarget | null;
  id: string;
  label: string;
  kind: FlowDiagramNodeKind;
  /**
   * Policy element `id`s this node covers. Empty when the node is purely
   * structural (inferred from a tool/variable definition with no policy
   * mention) — UI should render those nodes as non-clickable.
   *
   * Listed in document order (top of policy → bottom). The first entry is
   * the primary scroll target on click; all entries are highlighted to
   * visualize multi-span coverage.
   */
  target_ids: string[];
  children: FlowDiagramNode[];
}

export interface FlowDiagramResponse {
  classic_blocks?: FlowDiagramClassicBlock[];
  source_fingerprint?: string | null;
  /**
   * Id of the entity whose policy was outlined — per surface: widget
   * autoflow -> intent_workflow_id, email autopilot -> autopilot_workflow_id,
   * orchestrator -> orchestrator_autoflow_workflow_id.
   */
  entity_id: string;
  nodes: FlowDiagramNode[];
  generated_at: string;
  cached: boolean;
  error: 'empty_policy' | 'llm_failure' | null;
}

/** Selector for which workflow version the flow-diagram endpoints operate on. */
export interface FlowDiagramVersion {
  isDraft: boolean;
  versionType?: 'auto_saved' | 'saved' | 'published' | null;
  version?: number | null;
  isPublished?: boolean;
}

// === PolicyDiff: persisted, resumable improved-policy review (AA-3155) ===
//
// These back the persisted improved-policy review: GET
// /policy-diff/by-autoflow/{id} re-hydrates a review's hunk
// decisions and the snapshot used to anchor overlays, and PUT
// /policy-diff/{id}/save-progress flushes content + decisions.

export type PolicyDiffHunkDecision = 'pending' | 'accepted' | 'rejected';

export type PolicyDiffHunkOpType =
  | 'accept'
  | 'reject'
  | 'modify_improved'
  | 'modify_original';

/**
 * Hunk classification mirroring `HunkType` in the BE library. `text` /
 * `element` are generic fallbacks; the rest map to autoflow editor custom
 * Lexical nodes via `LEXICAL_NODE_TYPE_REGISTRY`.
 */
export type PolicyDiffHunkType =
  | 'text'
  | 'element'
  | 'variable_ref'
  | 'classic_block'
  | 'inline_block'
  | 'workflow_ref'
  | 'action_ref'
  | 'custom_event'
  | 'reroute';

/**
 * HTML payload for one side of a hunk. The BE stores an open dict, but in
 * practice it carries `html` (plus `insert_at_index` on added hunks).
 */
export interface PolicyDiffHunkContent {
  html: string;
  insert_at_index?: number;
}

/**
 * One ordered piece of a hunk's intra-line word diff (BE `WordDiffSegment`).
 *
 * Walk the list in order to render both sides of a modified hunk: a `keep`
 * segment shows on both sides, a `delete` only on the original side, an
 * `insert` only on the improved side. `kind` selects the payload - `text` for a
 * run of words, `node` for a single inline chip that is never split.
 */
export interface PolicyDiffWordDiffSegment {
  op: 'keep' | 'insert' | 'delete';
  kind: 'text' | 'node';
  text?: string;
  node?: { name: string; type: string };
}

/** A source a hunk's change was grounded in (BE `ResolvedAttributedSource`). */
export interface PolicyDiffAttributedSource {
  /**
   * The conversation this source resolves to: the `id` itself for a
   * `conversation` source, the underlying review's conversation for a
   * `human_feedback` source (null when that lookup fails), and null for
   * types with no conversation (tickets).
   */
  conversation_id: string | null;
  id: string;
  type: 'conversation' | 'ticket' | 'human_feedback';
}

/**
 * One side (added or removed) of a hunk's attribution. `explanation` can
 * stand alone when `sources` is empty (e.g. a stylistic wording change).
 */
export interface PolicyDiffHunkSideAttribution {
  explanation: string;
  sources: PolicyDiffAttributedSource[];
}

/**
 * Why a hunk was proposed, resolved by the BE from the recommendation's
 * source attributions. A modified hunk carries `modified` — one
 * explanation and one source list covering the rewrite as a whole. A pure
 * add or remove carries only the matching side (`added` or `removed`).
 * `modified` is null for older recommendations that stored the rewrite as
 * two independent attributions; those still surface via `added` + `removed`.
 */
export interface PolicyDiffHunkAttribution {
  added: PolicyDiffHunkSideAttribution | null;
  removed: PolicyDiffHunkSideAttribution | null;
  modified: PolicyDiffHunkSideAttribution | null;
}

/**
 * One hunk in a persisted review: immutable snapshot fields plus mutable
 * review state (the decision and any pre-decision edits to either side).
 */
export interface PolicyDiffHunkRecord {
  /**
   * Resolved per request from the recommendation (not persisted); null when
   * no attribution could be resolved for the hunk.
   */
  attribution: PolicyDiffHunkAttribution | null;
  decided_at: string | null;
  decided_by: string | null;
  decision: PolicyDiffHunkDecision;
  hunk_id: string;
  improved_content: PolicyDiffHunkContent | null;
  modified_improved: PolicyDiffHunkContent | null;
  modified_original: PolicyDiffHunkContent | null;
  operation: 'added' | 'removed' | 'modified';
  original_content: PolicyDiffHunkContent | null;
  target_xpath: string;
  type: PolicyDiffHunkType;
  /**
   * Ordered word-level segments for a modified hunk whose sides align closely
   * enough; null for added/removed hunks and near-total rewrites (render the
   * whole-block tint then). It describes the original proposal, so ignore it
   * for a side showing a reviewer edit (`modified_*`). Optional here (the BE
   * always sends it) to keep existing hunk fixtures valid; tighten to required
   * once they set it.
   */
  word_diff?: PolicyDiffWordDiffSegment[] | null;
}

// The enriched render-stream snapshot persisted on the PolicyDiff. Unlike the
// raw render stream, unchanged items carry `block_ids` (the
// stamped data-flow-ids used to anchor pending overlays), and changed items
// carry only `kind` + `hunk_id` (the full hunk lives in `hunks`).
export interface PolicyDiffSnapshotUnchangedItem {
  block_ids: string[];
  html: string;
  kind: 'unchanged';
}

export interface PolicyDiffSnapshotChangedItem {
  hunk_id: string;
  kind: 'added' | 'removed' | 'modified';
}

export interface PolicyDiffSnapshotSubtreeItem {
  children: PolicyDiffSnapshotItem[];
  close_html: string;
  kind: 'subtree';
  open_html: string;
}

export type PolicyDiffSnapshotItem =
  | PolicyDiffSnapshotUnchangedItem
  | PolicyDiffSnapshotChangedItem
  | PolicyDiffSnapshotSubtreeItem;

/** Returned by the PolicyDiff create / by-autoflow / save-progress endpoints. */
export interface PolicyDiffResponse {
  has_pending_hunks: boolean;
  hunks: PolicyDiffHunkRecord[];
  /** Reviewer-facing improvement highlights (mirrors AvailableRecommendation). */
  improvement_bytes: string[];
  intent_workflow_id: string;
  linked_autoflow_id: string;
  render_stream_snapshot: PolicyDiffSnapshotItem[];
  source_recommendation_id: string;
  /** Reviewer-facing what-changed summary, or null. */
  summary: string | null;
}

/**
 * Returned by /policy-diff/{id}/save-progress. A flush never adds or removes
 * hunks and never changes the render-stream snapshot or a hunk's attribution,
 * so only the hunks it touched come back (with attribution unset); merge them
 * into the held review by `hunk_id`, keeping the snapshot and each hunk's
 * attribution. `has_pending_hunks` reflects the whole review.
 */
export interface PolicyDiffSaveProgressResponse {
  batch_id: string;
  changed_hunks: PolicyDiffHunkRecord[];
  has_pending_hunks: boolean;
}

export interface PolicyDiffCreateRequest {
  intent_workflow_id: string;
  source_recommendation_id: string;
  version_name: string;
}

export interface PolicyDiffHunkOp {
  content?: PolicyDiffHunkContent | null;
  hunk_id: string;
  op: PolicyDiffHunkOpType;
}

export interface PolicyDiffSaveProgressRequest {
  action_source?: 'individual' | 'bulk';
  batch_id: string;
  hunk_ops: PolicyDiffHunkOp[];
  policy_description: string;
}

// === Improved policy on-demand generation ===
//
// These back the admin-triggered generation surface on the Improved policy
// tab: GET /improved-policy/{intent_workflow_id}/eligibility gates the
// Generate button, GET /improved-policy/{intent_workflow_id}/latest-job
// renders the current/last run, and POST
// /improved-policy/{intent_workflow_id}/generate enqueues a run.

/**
 * Why a workflow is not currently eligible for an on-demand generation run.
 * A closed set: the BE buckets anything else into a 503 rather than inventing
 * new codes, but render unknown codes through a generic fallback anyway.
 */
export type ImprovedPolicyGenerationReasonCode =
  | 'empty_policy'
  | 'insufficient_data'
  | 'job_in_flight'
  | 'no_published_version'
  | 'pending_recommendation_exists'
  | 'purpose_mismatch';

export interface ImprovedPolicyGenerationReason {
  code: ImprovedPolicyGenerationReasonCode;
  /**
   * Elaboration where the underlying check provides one. LLM-generated for
   * purpose_mismatch (≤500 chars) — untrusted, render as plain text only.
   */
  detail: string | null;
}

/**
 * Per-workflow signal counts behind the generation card. The review counts
 * describe reviewer engagement on the current published version;
 * usable_for_generation is the GEPA training-set size and includes
 * ticket-mined rows, so it can exceed the review counts.
 */
export interface ImprovedPolicyGenerationSignals {
  /** Conversations handled in the last 30 days, across published versions. */
  conversations_last_30d: number;
  /** Distinct submitted negative reviews on the current published version. */
  negative_reviews: number;
  /** Subset of negative_reviews with a substantive comment. */
  negative_reviews_with_comments: number;
  unlock_threshold: number;
  /** Unprocessed POLICY-typed classifications (review-derived + ticket-mined). */
  usable_for_generation: number;
}

export interface ImprovedPolicyGenerationEligibility {
  intent_workflow_id: string;
  is_eligible: boolean;
  reasons: ImprovedPolicyGenerationReason[];
  /** Populated even when ineligible, so the progress line always renders. */
  signals: ImprovedPolicyGenerationSignals;
  /** Published version the check ran against; null when nothing is published. */
  workflow_version: number | null;
  /** True only on staging; controls whether the file-upload widget is shown. */
  file_upload_enabled: boolean;
}

/** 201 response from POST /context-attachments. */
export interface ContextAttachmentResponse {
  char_count: number;
  file_id: string;
  filename: string;
}

export interface ContextAttachmentStatus extends ContextAttachmentResponse {
  /** Whether the staged file is still available for another run. */
  available: boolean;
}

/** Terminal states are completed, no_improvement, and failed. */
export type ImprovedPolicyGenerationJobState =
  | 'completed'
  | 'failed'
  | 'no_improvement'
  | 'running'
  | 'triggered';

export type ImprovedPolicyGenerationFailureReason =
  | 'enqueue_failed'
  | 'pipeline_error'
  | 'worker_timeout';

/** Latest-job row; the endpoint returns null when no run was ever triggered. */
export interface ImprovedPolicyGenerationJob {
  /** Combined context (free text + extracted file content) stored at trigger time. */
  additional_context: string | null;
  completed_at: string | null;
  /** File metadata from the previous run, available only for runs created with this field. */
  context_attachments?: ContextAttachmentStatus[];
  failure_reason: ImprovedPolicyGenerationFailureReason | null;
  intent_workflow_id: string;
  requested_at: string;
  requested_by: string;
  /** Opaque id of the resulting recommendation run; set on completed. */
  resulting_gepa_id: string | null;
  run_id: string;
  started_at: string | null;
  state: ImprovedPolicyGenerationJobState;
  /** Original typed guidance only; absent on jobs created before this field existed. */
  text_context?: string | null;
  /**
   * Version the run was scoped to. Can trail the current published version —
   * a republish does not cancel an in-flight run.
   */
  workflow_version: number;
}

/** 202 body from the generate endpoint; state is always 'triggered'. */
export interface TriggerImprovedPolicyGenerationResponse {
  intent_workflow_id: string;
  run_id: string;
  state: ImprovedPolicyGenerationJobState;
  workflow_version: number;
}

export type ImprovedPolicyGenerationErrorCode =
  | 'context_file_expired'
  | 'context_file_too_long'
  | 'context_too_long'
  | 'eligibility_failed'
  | 'enqueue_failed'
  | 'feature_disabled'
  | 'file_context_too_long'
  | 'no_published_version'
  | 'revalidation_in_flight'
  | 'run_in_flight'
  | 'transient_dependency_error';

/**
 * Error body shared by the generation endpoints. Discriminate on
 * payload.error_code, never on HTTP status — two 400s carry different codes.
 */
export interface ImprovedPolicyGenerationErrorBody {
  message: string;
  payload: {
    conflict_reason?: 'concurrent_trigger' | 'existing_rq_job';
    /** Embedded on eligibility_failed: the re-check that rejected the click. */
    eligibility?: ImprovedPolicyGenerationEligibility;
    error_code: ImprovedPolicyGenerationErrorCode;
  };
  status: number;
}

/**
 * Pusher payload for improved-policy-generation-status-change. Fires on
 * terminal transitions only and can repeat for the same run — de-duplicate on
 * (run_id, state). Deliberately minimal: refetch latest-job for the full row.
 */
export interface ImprovedPolicyGenerationStatusChangeEvent {
  intent_workflow_id: string;
  run_id: string;
  state: ImprovedPolicyGenerationJobState;
}
